import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import ErrorBoundary from '@components/ErrorBoundary';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { css } from '@linaria/core';
import { ExpandableText } from '@sharedComponents/ExpandableText';
import { TabContent } from '@sharedComponents/TabSwitcher';
import { trackTab } from '@sharedComponents/TabSwitcher/trackingTab';

import messages from '../../messages';
import { useIsRichContentFirstTab } from '../hooks/useIsRichContentFirstTab';
import { TabIds } from '../model';
import { ShowOnDesktop, ShowOnMobile, TitleWrapper } from '../styled';

import { ExpandButtonText } from './AboutProduct/styled';
import { useTabsWithExpandableText } from './ProductTabContentProvider';
import { RichContentArea } from './RichContentArea/loadable';
import { TAB_IDENTIFIERS, TRACKING_ATTRIBUTES } from './tracking';

interface RichTabContentProps {
  productFullName: string;
  customVersionKey: string;
  isReorderedRichContentVariant: boolean;
  tabOrder?: number;
}

export const RichTabContent: React.FC<RichTabContentProps> = ({
  productFullName,
  customVersionKey,
  isReorderedRichContentVariant,
  tabOrder,
}) => {
  const isRichContentFirstTab = useIsRichContentFirstTab();
  const { getTimeFromInit } = useTrackingContext();

  const tabsWithExpandableTextCtx = useTabsWithExpandableText();
  const { tabsWithExpandableTextState, toggleFullText } =
    tabsWithExpandableTextCtx ?? {};

  const richContentExpandableText =
    tabsWithExpandableTextState?.[TabIds.RichContent];

  const shouldShowFullText =
    richContentExpandableText?.shouldShowFullText ||
    !richContentExpandableText?.isTabOpenForTheFirstTime;

  const onToggleFullText = () => {
    toggleFullText?.(TabIds.RichContent);
    trackTab({
      mode: 'open partial',
      timing: getTimeFromInit(),
      trackingLabel: TRACKING_ATTRIBUTES.richContent,
    });
  };

  return (
    <TabContent
      lazyLoad={!isRichContentFirstTab}
      tabIdentifier={TAB_IDENTIFIERS.richContent}
      trackingLabel={TRACKING_ATTRIBUTES.richContent}
      tabDividerMobile={isReorderedRichContentVariant}
      tabId={TabIds.RichContent}
      tabOrder={tabOrder}
      title={
        <TitleWrapper>
          <ShowOnDesktop>
            <FormattedMessage
              {...messages.rcHeader}
              values={{
                name: productFullName,
              }}
            />
          </ShowOnDesktop>

          <ShowOnMobile>
            <FormattedMessage {...messages.fixedHeaderRichContent} />
          </ShowOnMobile>
        </TitleWrapper>
      }
    >
      <ErrorBoundary>
        <ExpandableText
          onClick={onToggleFullText}
          useOnlyOn="mobile"
          isExpanded={shouldShowFullText}
          disabled={!isRichContentFirstTab}
          height="400px"
          gradientClassName={css`
            max-height: 50%;
          `}
          buttonText={
            <ExpandButtonText>
              <FormattedMessage {...messages.showMoreText} />
            </ExpandButtonText>
          }
        >
          <RichContentArea versionKey={customVersionKey} />
        </ExpandableText>
      </ErrorBoundary>
    </TabContent>
  );
};
